import {
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
} from 'react-hook-form'

import { exists } from './exists'

/**
 * オブジェクトから指定されたパスの値を返します。
 * オブジェクトやパスの値が null または undefined の場合は undefined を返します。
 *
 * @template T - オブジェクトの型。
 * @template P - パスの型。
 * @param obj - オブジェクト。
 * @param path - パス。
 * @returns オブジェクトのパスの値。
 */
export function getByPath<T extends FieldValues, P extends FieldPath<T>>(
  obj: T | null | undefined,
  path: P,
): FieldPathValue<T, P> | undefined

/**
 * オブジェクトから指定されたパスの値を返します。
 * オブジェクトやパスの値が null または undefined の場合は undefined を返します。
 *
 * @template T - オブジェクトの型。
 * @template P - パスの型。
 * @param path - パス。
 * @returns オブジェクトを引数に取り、オブジェクトのパスの値を返す関数。
 */
export function getByPath<T extends FieldValues, P extends FieldPath<T>>(
  path: P,
): (obj: T | null | undefined) => FieldPathValue<T, P> | undefined

/**
 * @param args - 引数。
 * @returns 結果または部分適用された関数。
 */
export function getByPath<T extends FieldValues, P extends FieldPath<T>>(
  ...args: [P] | [T | null | undefined, P]
):
  | FieldPathValue<T, P>
  | ((obj: T | null | undefined) => FieldPathValue<T, P> | undefined)
  | undefined {
  const getByPathInner = (
    obj: T | null | undefined,
    path: P,
  ): FieldPathValue<T, P> | undefined => {
    const keys = path.split(/\.+?/)
    const result = keys.reduce(
      (prev, key) => (exists(prev) ? prev[key] : prev),
      obj,
    )

    return result as FieldPathValue<T, P> | undefined
  }

  if (args.length === 1) {
    return (obj: T | null | undefined) => getByPathInner(obj, ...args)
  }

  return getByPathInner(...args)
}
